/**
 * Route guards (STANDARDSv2 §Frontend Route Guards).
 *
 * `ProtectedRoute` must wait for the auth bootstrap to finish before deciding. Redirecting while
 * `isLoading` is still true is the second-most-common cause of "SSO works but I land back on
 * /login" — the token is present but the user object hasn't hydrated yet.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { FullPageSpinner } from './FullPageSpinner';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner label="Loading your session…" />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/** Requires the admin capability. Non-admins are redirected, and the nav item is hidden too. */
export function AdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isAdmin } = useAuth();

  if (isLoading) return <FullPageSpinner label="Loading your session…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

/** Requires a specific permission. The API enforces it independently regardless. */
export function PermissionRoute({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();

  if (isLoading) return <FullPageSpinner label="Loading your session…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasPermission(permission)) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

/** Keeps authenticated users off the login page. */
export function GuestRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner label="Loading…" />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}
